import { useEffect, useMemo, useState } from 'react';
import { ArrowRight, Building2, Check, RefreshCw, Search, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import {
  circleApi,
  groupApi,
  type CircleDetail,
  type CircleType,
  type GroupCommercialSummaryResponse,
  type GroupCommercialTenantSummary,
  type GroupTenant,
  type TenantGroup,
} from '@/lib/network-api';
import { cn } from '@/lib/utils';

type TenantOption = {
  id: string;
  name: string;
  slug?: string;
};

type CompanyRow = {
  id: string;
  name: string;
  displayName?: string;
  slug?: string;
  status: string | null;
  groupId?: string;
  groupName?: string;
  countryCode?: string | null;
  entityType?: string | null;
  defaultCurrency?: string | null;
  activeUsers?: number;
  totalUsers?: number;
  jobsToday?: number;
  jobsMonthToDate?: number;
  billedToday?: number;
  unresolvedExceptions?: number;
  dataStatus?: GroupCommercialTenantSummary['dataStatus'];
  healthStatus?: GroupCommercialTenantSummary['healthStatus'];
};

type GroupWithSummary = TenantGroup & {
  tenants: GroupTenant[];
  commercialSummary?: GroupCommercialSummaryResponse | null;
};

type CompanySection = {
  id: string;
  title: string;
  subtitle?: string;
  kind: 'ownership' | 'circle' | 'fallback';
  rows: CompanyRow[];
};

const SOFT_DELETED_TENANT_IDS = new Set(['00000000-0000-4000-a000-000000000023']);
const SOFT_DELETED_TENANT_SLUGS = new Set(['penanshin-shipping']);

function isSoftDeletedTenant(tenant: { id?: string | null; slug?: string | null; tenant_slug?: string | null }): boolean {
  return Boolean(
    (tenant.id && SOFT_DELETED_TENANT_IDS.has(tenant.id))
    || (tenant.slug && SOFT_DELETED_TENANT_SLUGS.has(tenant.slug))
    || (tenant.tenant_slug && SOFT_DELETED_TENANT_SLUGS.has(tenant.tenant_slug)),
  );
}

function formatMoney(value: number | undefined, currency?: string | null) {
  return new Intl.NumberFormat('en-SG', {
    style: 'currency',
    currency: currency || 'SGD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number.isFinite(value) ? value! : 0);
}

function healthClass(health?: GroupCommercialTenantSummary['healthStatus']) {
  if (health === 'attention') return 'border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-300';
  if (health === 'active') return 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-950/30 dark:text-emerald-300';
  return 'border-border bg-muted/50 text-muted-foreground';
}

function statusDotClass(health?: GroupCommercialTenantSummary['healthStatus']) {
  if (health === 'attention') return 'bg-amber-500 shadow-[0_0_0_3px_rgba(245,158,11,0.14)]';
  if (health === 'active') return 'bg-emerald-500 shadow-[0_0_0_3px_rgba(16,185,129,0.14)]';
  return 'bg-slate-400 shadow-[0_0_0_3px_rgba(148,163,184,0.16)]';
}

function numberOrDash(value: number | undefined) {
  return typeof value === 'number' ? value.toLocaleString() : '—';
}

function displayGroupName(name: string): string {
  return name.replace(/\s+\(([^)]+)\)$/, ' - $1');
}

function displayCircleName(name: string, type: CircleType): string {
  const cleaned = name.trim().replace(/\s+/g, ' ');
  if (type === 'agent_network' && /^[a-z\s-]+$/.test(cleaned)) {
    const title = cleaned.replace(/\b\w/g, (match) => match.toUpperCase());
    if (/\bNetwork$/i.test(title) && !/\b(Agent|Agency)\b/i.test(title)) {
      return title.replace(/\bNetwork$/i, 'Agency Network');
    }
    return title;
  }
  return cleaned;
}

function circleTypeLabel(type: CircleType): string {
  switch (type) {
    case 'agent_network':
      return 'Network';
    default:
      return 'Network';
  }
}

function groupLinkedCircleId(group: GroupWithSummary): string | null {
  const value = group.config?.['circle_id'];
  return typeof value === 'string' && value.trim() ? value : null;
}

function isAgentNetworkBackedGroup(group: GroupWithSummary, circles: CircleDetail[]): boolean {
  const linkedCircleId = groupLinkedCircleId(group);
  if (!linkedCircleId) return false;
  return circles.some((circle) => circle.id === linkedCircleId && circle.circle_type === 'agent_network');
}

function displayEntityType(type?: string | null): string | null {
  if (!type) return null;
  return type
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
}

function displayDuplicateCompanyName(row: CompanyRow): string {
  const normalizedName = row.name.trim().toLowerCase();
  if (normalizedName === 'penanshin shipping pte ltd') {
    return row.name;
  }
  const qualifier = displayEntityType(row.entityType) ?? row.slug ?? row.countryCode ?? row.id.slice(0, 8);
  return `${row.name} (${qualifier})`;
}

function displayCompanyMetaLabel(row: CompanyRow): string | undefined {
  if (row.slug === 'penanshin' && row.name.trim().toLowerCase() === 'penanshin shipping pte ltd') {
    return 'penanshin shipping';
  }
  return row.slug;
}

function CompanyRowItem({
  row,
  currentTenantId,
  switching,
  onSwitch,
}: {
  row: CompanyRow;
  currentTenantId?: string;
  switching: boolean;
  onSwitch: (tenantId: string, groupId?: string) => void;
}) {
  const { t } = useTranslation();
  const isCurrent = row.id === currentTenantId;
  const statusLabel = row.dataStatus === 'unavailable'
    ? t('networkPage.groups.filterUnavailable', { defaultValue: 'Unavailable' })
    : (row.healthStatus ?? row.status ?? 'active');
  const metaLabel = displayCompanyMetaLabel(row);
  const displayType = row.slug === 'penanshin' ? null : row.entityType;

  return (
    <div
      className={cn(
        'group relative border-b bg-background transition-colors last:border-b-0 hover:bg-muted/35',
        isCurrent && 'bg-primary/[0.045]',
      )}
    >
      {isCurrent && <div className="absolute inset-y-0 left-0 w-1 bg-primary" />}
      <div className="grid items-center gap-2 px-3 py-3 md:grid-cols-[minmax(414px,2.43fr)_86px_70px_58px_92px_76px_64px] md:px-4">
        <div className="flex min-w-0 items-center gap-3">
          <div className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border', isCurrent ? 'border-primary/20 bg-primary/10 text-primary' : 'border-border bg-muted/50 text-muted-foreground')}>
            <Building2 className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <div className="flex min-w-0 items-center gap-2">
              <div className="truncate text-sm font-medium text-foreground">{row.displayName ?? row.name}</div>
              {isCurrent && <Badge variant="outline" className="h-5 gap-1 border-primary/30 px-1.5 text-[10px] text-primary"><Check className="h-3 w-3" />{t('networkPage.groups.current', { defaultValue: 'Current' })}</Badge>}
            </div>
            <div className="mt-1 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-muted-foreground">
              {metaLabel && <span className="font-mono text-[10px] text-muted-foreground/90">{metaLabel}</span>}
              {displayType && <span className="capitalize">{displayType}</span>}
              {row.countryCode && <span className="font-medium text-muted-foreground/90">{row.countryCode}</span>}
              {row.groupName && <span className="max-w-[360px] truncate">{row.groupName}</span>}
            </div>
          </div>
        </div>
        <div className="min-w-0 text-xs">
          <div className="text-[10px] uppercase text-muted-foreground md:hidden">{t('tenantSwitch.status', { defaultValue: 'Status' })}</div>
          <div className={cn('inline-flex max-w-full items-center gap-2 rounded-full border px-2 py-1 text-[11px] capitalize', healthClass(row.healthStatus))}>
            <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', statusDotClass(row.healthStatus))} />
            <span className="truncate">{statusLabel}</span>
          </div>
        </div>
        <div className="text-xs md:text-right">
          <div className="text-[10px] uppercase text-muted-foreground md:hidden">{t('tenantSwitch.activeUsers', { defaultValue: 'Active users' })}</div>
          <div className="font-medium tabular-nums text-foreground">{numberOrDash(row.activeUsers)} / {numberOrDash(row.totalUsers)}</div>
        </div>
        <div className="text-xs md:text-right">
          <div className="text-[10px] uppercase text-muted-foreground md:hidden">{t('tenantSwitch.jobsToday', { defaultValue: 'Jobs today' })}</div>
          <div className="font-medium tabular-nums text-foreground">{numberOrDash(row.jobsToday)}</div>
          <div className="text-[10px] tabular-nums text-muted-foreground">
            {t('tenantSwitch.jobsMonthToDateShort', { defaultValue: 'MTD' })}: {numberOrDash(row.jobsMonthToDate)}
          </div>
        </div>
        <div className="text-xs md:text-right">
          <div className="text-[10px] uppercase text-muted-foreground md:hidden">{t('tenantSwitch.billedToday', { defaultValue: 'Billed today' })}</div>
          <div className="font-medium tabular-nums text-foreground">{formatMoney(row.billedToday, row.defaultCurrency)}</div>
        </div>
        <div className="text-xs md:text-right">
          <div className="text-[10px] uppercase text-muted-foreground md:hidden">{t('tenantSwitch.openExceptions', { defaultValue: 'Open exceptions' })}</div>
          <div className={cn('font-medium tabular-nums', (row.unresolvedExceptions ?? 0) > 0 ? 'text-amber-700 dark:text-amber-300' : 'text-foreground')}>
            {numberOrDash(row.unresolvedExceptions)}
          </div>
        </div>
        <div className="flex justify-start md:justify-end">
          {!isCurrent && row.status !== 'suspended' && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={switching}
              onClick={() => onSwitch(row.id, row.groupId)}
              className="h-8 shrink-0 gap-1 px-2 text-primary hover:bg-primary/10 hover:text-primary"
            >
              {t('networkPage.groups.switch', { defaultValue: 'Switch' })}
              <ArrowRight className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export function TenantSwitchPanel({
  currentTenantId,
  tenantName,
  tenants,
  switching,
  onSwitch,
}: {
  currentTenantId?: string;
  tenantName: string;
  tenants: TenantOption[];
  switching: boolean;
  onSwitch: (tenantId: string, groupId?: string) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState<GroupWithSummary[]>([]);
  const [circles, setCircles] = useState<CircleDetail[]>([]);
  const [query, setQuery] = useState('');
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);

  const loadGroups = () => {
    setLoading(true);
    const accessibleTenantIds = tenants
      .filter((tenant) => !isSoftDeletedTenant(tenant))
      .map((tenant) => tenant.id);
    Promise.all([
      groupApi.list().catch(() => []),
      circleApi.list().catch(() => []),
    ])
      .then(async ([list, circleList]) => {
        const detailed = await Promise.all(list.map(async (group) => {
          const [detail, summary] = await Promise.all([
            groupApi.get(group.id, { tenantIds: accessibleTenantIds }).catch(() => ({ ...group, tenants: [] })),
            accessibleTenantIds.length > 0
              ? groupApi.getCommercialSummary(group.id, { tenantIds: accessibleTenantIds }).catch(() => null)
              : Promise.resolve(null),
          ]);
          return {
            ...(detail as TenantGroup & { tenants: GroupTenant[] }),
            commercialSummary: summary,
          };
        }));
        setGroups(detailed);
        const circleDetails = await Promise.all(
          circleList
            .filter((circle) => circle.status === 'active')
            .filter((circle) => circle.circle_type === 'agent_network')
            .map((circle) => circleApi.get(circle.id, { tenantIds: accessibleTenantIds }).catch(() => null)),
        );
        setCircles(circleDetails.filter((circle): circle is CircleDetail => Boolean(circle)));
      })
      .catch(() => {
        setGroups([]);
        setCircles([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (open) loadGroups();
  }, [open]);

  const ownershipGroups = useMemo(
    () => groups.filter((group) => !isAgentNetworkBackedGroup(group, circles)),
    [circles, groups],
  );

  const rows = useMemo<CompanyRow[]>(() => {
    const byId = new Map<string, CompanyRow>();
    const accessibleTenants = tenants.filter((tenant) => !isSoftDeletedTenant(tenant));
    const accessibleTenantIds = new Set(accessibleTenants.map((tenant) => tenant.id));
    if (currentTenantId) accessibleTenantIds.add(currentTenantId);

    for (const tenant of accessibleTenants) {
      byId.set(tenant.id, {
        id: tenant.id,
        name: tenant.name,
        slug: tenant.slug,
        status: 'active',
      });
    }

    for (const group of ownershipGroups) {
      const summaryByTenant = new Map((group.commercialSummary?.tenants ?? []).map((tenant) => [tenant.tenantId, tenant]));
      for (const tenant of group.tenants ?? []) {
        if (isSoftDeletedTenant({ id: tenant.id, slug: tenant.slug })) continue;
        if (!accessibleTenantIds.has(tenant.id)) continue;
        const summary = summaryByTenant.get(tenant.id);
        byId.set(tenant.id, {
          id: tenant.id,
          name: tenant.name,
          slug: tenant.slug,
          status: tenant.status ?? summary?.status ?? null,
          groupId: group.id,
          groupName: group.name,
          countryCode: tenant.country_code ?? summary?.countryCode ?? null,
          entityType: tenant.entity_type ?? summary?.entityType ?? null,
          defaultCurrency: tenant.default_currency ?? summary?.defaultCurrency ?? null,
          activeUsers: summary?.activeUsers,
          totalUsers: summary?.totalUsers,
          jobsToday: summary?.jobsToday,
          jobsMonthToDate: summary?.jobsMonthToDate,
          billedToday: summary?.billedToday,
          unresolvedExceptions: summary?.unresolvedExceptions,
          dataStatus: summary?.dataStatus,
          healthStatus: summary?.healthStatus,
        });
      }
    }

    for (const circle of circles) {
      const summaryByTenant = new Map((circle.commercialSummary?.tenants ?? []).map((tenant) => [tenant.tenantId, tenant]));
      for (const member of circle.members ?? []) {
        if (member.status !== 'active' || !member.tenant_id) continue;
        if (isSoftDeletedTenant({ id: member.tenant_id, tenant_slug: member.tenant_slug })) continue;
        if (!accessibleTenantIds.has(member.tenant_id)) continue;
        const summary = summaryByTenant.get(member.tenant_id);
        const existing = byId.get(member.tenant_id);
        byId.set(member.tenant_id, {
          id: member.tenant_id,
          name: existing?.name ?? member.tenant_name ?? summary?.name ?? member.external_name ?? t('tenantSwitch.unknownCompany', { defaultValue: 'Unknown company' }),
          displayName: existing?.displayName,
          slug: existing?.slug ?? member.tenant_slug ?? summary?.slug,
          status: existing?.status ?? member.tenant_status ?? summary?.status ?? member.status,
          groupId: existing?.groupId,
          groupName: existing?.groupName,
          countryCode: existing?.countryCode ?? member.country_code ?? summary?.countryCode ?? null,
          entityType: existing?.entityType ?? member.tenant_entity_type ?? summary?.entityType ?? null,
          defaultCurrency: existing?.defaultCurrency ?? member.tenant_default_currency ?? summary?.defaultCurrency ?? null,
          activeUsers: existing?.activeUsers ?? summary?.activeUsers,
          totalUsers: existing?.totalUsers ?? summary?.totalUsers,
          jobsToday: existing?.jobsToday ?? summary?.jobsToday,
          jobsMonthToDate: existing?.jobsMonthToDate ?? summary?.jobsMonthToDate,
          billedToday: existing?.billedToday ?? summary?.billedToday,
          unresolvedExceptions: existing?.unresolvedExceptions ?? summary?.unresolvedExceptions,
          dataStatus: existing?.dataStatus ?? summary?.dataStatus,
          healthStatus: existing?.healthStatus ?? summary?.healthStatus,
        });
      }
    }

    const unsortedRows = Array.from(byId.values());
    const nameCounts = unsortedRows.reduce((counts, row) => {
      const key = row.name.trim().toLowerCase();
      counts.set(key, (counts.get(key) ?? 0) + 1);
      return counts;
    }, new Map<string, number>());
    const displayRows = unsortedRows.map((row) => {
      const isDuplicateName = (nameCounts.get(row.name.trim().toLowerCase()) ?? 0) > 1;
      if (!isDuplicateName) return row;
      return {
        ...row,
        displayName: displayDuplicateCompanyName(row),
      };
    });

    return displayRows.sort((a, b) => {
      if (a.id === currentTenantId) return -1;
      if (b.id === currentTenantId) return 1;
      return a.name.localeCompare(b.name);
    });
  }, [circles, currentTenantId, ownershipGroups, t, tenants]);

  const sections = useMemo<CompanySection[]>(() => {
    const rowByTenantId = new Map(rows.map((row) => [row.id, row]));
    const result: CompanySection[] = [];

    for (const group of ownershipGroups) {
      const groupRows = (group.tenants ?? [])
        .map((tenant) => rowByTenantId.get(tenant.id))
        .filter((row): row is CompanyRow => Boolean(row));
      if (groupRows.length === 0) continue;
      result.push({
        id: `group:${group.id}`,
        title: displayGroupName(group.name),
        subtitle: t('tenantSwitch.ownershipGroup', { defaultValue: 'Ownership group' }),
        kind: 'ownership',
        rows: groupRows,
      });
    }

    for (const circle of circles) {
      const circleRows = (circle.members ?? [])
        .filter((member) => member.status === 'active' && member.tenant_id)
        .map((member) => rowByTenantId.get(member.tenant_id!))
        .filter((row): row is CompanyRow => Boolean(row));
      if (circleRows.length === 0) continue;
      result.push({
        id: `circle:${circle.id}`,
        title: displayCircleName(circle.name, circle.circle_type),
        subtitle: circleTypeLabel(circle.circle_type),
        kind: 'circle',
        rows: circleRows,
      });
    }

    if (result.length === 0 && rows.length > 0) {
      return [{
        id: 'fallback:all',
        title: t('tenantSwitch.availableCompanies', { defaultValue: 'Available companies' }),
        subtitle: t('tenantSwitch.directLoginAccess', { defaultValue: 'Direct login access' }),
        kind: 'fallback',
        rows,
      }];
    }
    return result;
  }, [circles, ownershipGroups, rows, t]);

  useEffect(() => {
    if (sections.length === 0) {
      setActiveSectionId(null);
      return;
    }
    const currentStillValid = activeSectionId && sections.some((section) => section.id === activeSectionId);
    if (currentStillValid) return;
    const currentSection = sections.find((section) => section.rows.some((row) => row.id === currentTenantId));
    setActiveSectionId(currentSection?.id ?? sections[0]!.id);
  }, [activeSectionId, currentTenantId, sections]);

  const activeSection = sections.find((section) => section.id === activeSectionId) ?? sections[0] ?? null;
  const visibleRows = (() => {
    if (!activeSection) return [];
    const term = query.trim().toLowerCase();
    if (!term) return activeSection.rows;
    return activeSection.rows.filter((row) =>
        `${activeSection.title} ${activeSection.subtitle ?? ''} ${row.displayName ?? row.name} ${row.slug ?? ''} ${row.groupName ?? ''} ${row.entityType ?? ''} ${row.countryCode ?? ''}`
          .toLowerCase()
          .includes(term),
      );
  })();

  const handleSwitch = async (tenantId: string, groupId?: string) => {
    await onSwitch(tenantId, groupId);
    setOpen(false);
  };

  return (
    <div className="flex min-w-0 items-center gap-1.5">
      <Button
        type="button"
        size="icon"
        variant="outline"
        disabled={switching}
        onClick={() => setOpen(true)}
        className="h-7 w-7 shrink-0"
        title={t('tenantSwitch.open', { defaultValue: 'Switch company' })}
      >
        <Building2 className="h-3.5 w-3.5" />
      </Button>
      <button
        type="button"
        disabled={switching}
        onClick={() => setOpen(true)}
        className="min-w-0 text-left text-sm font-medium leading-tight text-foreground hover:text-primary disabled:cursor-wait disabled:opacity-50"
        title={tenantName}
      >
        <span className="block whitespace-normal break-words">{tenantName}</span>
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="flex w-full flex-col overflow-hidden p-0 sm:max-w-[1040px]">
          <SheetHeader className="border-b bg-muted/20 px-5 py-4">
            <div className="flex items-start justify-between gap-4 pr-8">
              <div>
                <SheetTitle>{t('tenantSwitch.title', { defaultValue: 'Switch company' })}</SheetTitle>
                <SheetDescription>
                  {t('tenantSwitch.description', { defaultValue: 'Pick the target company. Today metrics are shown on each row.' })}
                </SheetDescription>
              </div>
              <Button type="button" variant="outline" size="sm" onClick={loadGroups} disabled={loading} className="h-8 gap-1.5">
                <RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} />
                {t('networkPage.common.refresh', { defaultValue: 'Refresh' })}
              </Button>
            </div>
          </SheetHeader>

          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
            <div className="space-y-3">
              <div className="flex items-center gap-2 rounded-lg border bg-background px-3 py-2 shadow-sm">
                <Search className="h-4 w-4 text-muted-foreground" />
                <input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder={t('tenantSwitch.search', { defaultValue: 'Search company, group, country...' })}
                  className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                />
                {query && (
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={() => setQuery('')}
                    className="h-6 w-6 shrink-0 text-muted-foreground hover:bg-muted hover:text-foreground"
                    aria-label={t('common.clear', { defaultValue: 'Clear' })}
                    title={t('common.clear', { defaultValue: 'Clear' })}
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
              {sections.length > 0 && (
                <div className="-mx-1 overflow-x-auto pb-1">
                  <div className="flex min-w-max gap-2 px-1">
                    {sections.map((section) => {
                      const active = section.id === activeSection?.id;
                      return (
                        <button
                          key={section.id}
                          type="button"
                          role="tab"
                          aria-selected={active}
                          onClick={() => setActiveSectionId(section.id)}
                          className={cn(
                            'group flex min-w-[180px] items-center justify-between gap-3 rounded-xl border px-3 py-2 text-left transition-all',
                            active
                              ? 'border-primary/40 bg-primary/[0.08] shadow-sm ring-1 ring-primary/15'
                              : 'border-border bg-background hover:border-primary/25 hover:bg-muted/35',
                          )}
                        >
                          <span className="min-w-0">
                            <span className={cn('block truncate text-sm font-medium', active ? 'text-primary' : 'text-foreground')}>
                              {section.title}
                            </span>
                            {section.subtitle && (
                              <span className="mt-0.5 block truncate text-[11px] text-muted-foreground">
                                {section.subtitle}
                              </span>
                            )}
                          </span>
                          <span className={cn(
                            'flex h-6 min-w-6 shrink-0 items-center justify-center rounded-full px-2 text-[11px] font-semibold tabular-nums',
                            active ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary',
                          )}>
                            {section.rows.length}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="overflow-hidden rounded-xl border bg-background shadow-sm">
                <div className="hidden grid-cols-[minmax(414px,2.43fr)_86px_70px_58px_92px_76px_64px] gap-2 border-b bg-muted/45 px-4 py-2 text-[11px] uppercase text-muted-foreground md:grid">
                  <div>{t('tenantSwitch.company', { defaultValue: 'Company' })}</div>
                  <div>{t('tenantSwitch.status', { defaultValue: 'Status' })}</div>
                  <div className="text-right">{t('tenantSwitch.activeUsers', { defaultValue: 'Users' })}</div>
                  <div className="text-right">{t('tenantSwitch.jobsTodayMonthToDate', { defaultValue: 'Jobs T/MTD' })}</div>
                  <div className="text-right">{t('tenantSwitch.billedToday', { defaultValue: 'Billed' })}</div>
                  <div className="text-right">{t('tenantSwitch.openExceptions', { defaultValue: 'Exceptions' })}</div>
                  <div />
                </div>
                {loading ? (
                  <div className="space-y-1 p-2">
                    {[1, 2, 3].map((item) => <Skeleton key={item} className="h-16 rounded-lg" />)}
                  </div>
                ) : !activeSection || visibleRows.length === 0 ? (
                  <div className="py-12 text-center text-sm text-muted-foreground">
                    {t('tenantSwitch.empty', { defaultValue: 'No company available.' })}
                  </div>
                ) : (
                  <div>
                    <div className={cn(
                      'flex items-center justify-between gap-3 border-b px-4 py-2',
                      activeSection.kind === 'circle' ? 'bg-blue-50/70 dark:bg-blue-950/20' : 'bg-muted/30',
                    )}>
                      <div className="min-w-0">
                        <div className="truncate text-xs font-semibold uppercase tracking-wide text-foreground">{activeSection.title}</div>
                        {activeSection.subtitle && <div className="mt-0.5 text-[11px] text-muted-foreground">{activeSection.subtitle}</div>}
                      </div>
                      <Badge variant="outline" className="shrink-0 text-[10px]">
                        {visibleRows.length} / {activeSection.rows.length}
                      </Badge>
                    </div>
                    {visibleRows.map((row) => (
                      <CompanyRowItem
                        key={`${activeSection.id}:${row.id}`}
                        row={row}
                        currentTenantId={currentTenantId}
                        switching={switching}
                        onSwitch={handleSwitch}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
